import React, { useState } from 'react';
import { toast } from '@cotera/client/app/components';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useToolsV2ProviderToolsList } from '@cotera/client/app/hooks/use-available-tools';
import { OAuthConnectButton } from '@cotera/client/app/components/tools';
import {
  getCredentialStrategyOauthProvider,
  type ToolListItemResponse,
} from '@cotera/api';
import { Assert } from '@cotera/utilities';
import { useAuth } from '@workos-inc/authkit-react';
import {
  ConnectedStatusCard,
  InlineDeleteConfirmation,
} from '../credential-connection-cards';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import type { CredentialStrategyViewProps } from './credential-strategy-view-props';

export const WorkosPipeStrategyView: React.FC<CredentialStrategyViewProps> = ({
  toolDisplayName,
  strategy,
  provider,
  toolKey,
  onSuccess,
}) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const inner = strategy.strategy;
  const oauthProvider = getCredentialStrategyOauthProvider(inner);
  const providerKey = oauthProvider ?? toolDisplayName.toLowerCase();
  const providerTools = useToolsV2ProviderToolsList(provider);
  const toolRow = providerTools.find(
    (t: ToolListItemResponse) => t.key === toolKey
  );
  const isConnected = toolRow?.canExecute === true;

  const [showConfirm, setShowConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const { user } = useAuth();

  if (!isConnected) {
    return (
      <OAuthConnectButton
        displayName={toolDisplayName}
        provider={oauthProvider}
        onConnected={async (event) => {
          if (
            event.connectionState === 'already-connected' ||
            event.connectionState === 'newly-connected'
          ) {
            const syncResult = await client.toolsV2.syncWorkosProviderAccount({
              provider: providerKey,
            });
            Assert.assertOk(syncResult);
          }

          emitToolCredentialUpdated(orgId, provider);

          if (
            event.connectionState === 'already-connected' ||
            event.connectionState === 'newly-connected'
          ) {
            onSuccess();
          }
        }}
      />
    );
  }

  const handleDisconnect = async () => {
    if (user === null) {
      toast.error('Sign in to disconnect');
      return;
    }

    setIsDisconnecting(true);
    try {
      const disconnectResult = await client.toolsV2.disconnectWorkosProvider({
        provider: providerKey,
      });
      Assert.assertOk(disconnectResult);
      emitToolCredentialUpdated(orgId, provider);
      toast.success('Credentials deleted');
      setShowConfirm(false);
    } catch {
      toast.error('Failed to delete credentials');
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <ConnectedStatusCard
        title={`Connected to ${toolDisplayName}`}
        description="Authenticated via OAuth"
        variant="default"
        isActive={true}
        onDelete={() => setShowConfirm((v) => !v)}
      />
      {showConfirm && (
        <InlineDeleteConfirmation
          isPending={isDisconnecting}
          onConfirm={handleDisconnect}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
};
